/**
 * WechatBindingManagementSection — Issue #269.
 *
 * Renders the admin-side WeChat login & binding management module inside
 * `/settings`. Replaces the prior `wechat_binding` "待 Issue 落地" placeholder.
 *
 * Capabilities:
 *   - Mask-only binding summary list with risk-flag chips.
 *   - Customer-service work-order create / submit / approve / reject /
 *     cancel / execute lifecycle. Approve / reject / execute controls are
 *     additionally gated client-side; the backend re-validates so a UI
 *     bypass cannot escalate.
 *   - Per-tenant/org WeChat login policy toggle with the mandatory
 *     fallback-method check.
 *
 * Red lines:
 *   - Never renders raw openid / unionid / full phone. Only the
 *     `openidHashTail` / `unionidHashTail` and nickname mask from the
 *     backend.
 *   - Never optimistically fakes state. All success paths re-fetch the
 *     server-rendered view.
 *   - When the caller lacks any wechat_* permission the component hides
 *     itself entirely (returns null) so the rest of the settings page is
 *     unaffected.
 */
import { useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertTriangle,
  Inbox,
  KeyRound,
  MessageCircle,
  ShieldCheck,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useWechatBindingManagement,
  type AsyncState,
} from '@/hooks/useWechatBindingManagement';
import {
  canApproveWechatBindingWorkOrders,
  canReadWechatBindingManagement,
  canWriteWechatBindingWorkOrders,
  canWriteWechatLoginPolicy,
} from '@/lib/wechatBindingPermissions';
import type {
  AdminWechatBindingSummaryDto,
  AdminWechatBindingWorkOrderDto,
  AdminWechatLoginPolicyDto,
} from '@jinzhuan/api-client';

const REQUEST_TYPE_OPTIONS = [
  { value: 'unbind', label: '解绑' },
  { value: 'rebind', label: '换绑' },
  { value: 'merge_account_review', label: '账号合并复核' },
] as const;

const FALLBACK_METHOD_OPTIONS = [
  { value: 'phone_password', label: '手机号 + 密码' },
  { value: 'phone_sms', label: '手机号 + 短信验证码' },
] as const;

const RISK_FLAG_LABEL: Record<string, string> = {
  multi_account_same_wechat: '同一微信绑定多个账号',
  multi_wechat_same_phone: '同一账号存在多条活跃绑定',
  no_recent_sms_verification: '账号无手机号验证',
  recent_account_recovery: '近期发生过账号恢复',
  pending_work_order: '已有进行中的工单',
};

const STATUS_LABEL: Record<string, string> = {
  active: '已绑定',
  unbound: '已解绑',
  pending_review: '工单进行中',
  conflict: '存在冲突',
  draft: '草稿',
  pending_approval: '待审批',
  approved: '待执行',
  rejected: '已驳回',
  executed: '已执行',
  cancelled: '已取消',
};

const RAW_PHONE_PATTERN = /\d{11}/;
const RAW_OPENID_PATTERN = /[A-Za-z0-9_-]{28,}/;

const createSchema = z.object({
  customerAccountId: z
    .string()
    .min(1, '请输入客户账号 ID')
    .max(50, '客户账号 ID 长度超出限制'),
  bindingId: z.string().max(50).optional().or(z.literal('')),
  requestType: z.enum(['unbind', 'rebind', 'merge_account_review']),
  reason: z.string().min(4, '请说明原因').max(1000, '原因过长'),
  targetBindingSummary: z
    .string()
    .min(1, '请填写脱敏摘要')
    .max(255, '摘要过长')
    .refine((v) => !RAW_PHONE_PATTERN.test(v), '不允许包含完整 11 位手机号')
    .refine(
      (v) => !RAW_OPENID_PATTERN.test(v),
      '不允许包含原始 OpenID/UnionID（28+ 字符）',
    ),
  authorizationAttachmentIdsText: z
    .string()
    .min(1, '请填写至少一个附件中心 ID（逗号分隔）'),
});

type CreateFormValues = z.infer<typeof createSchema>;

const submitSchema = z.object({
  workOrderId: z.string().min(1),
  smsChallengeId: z.string().min(1, '请填写短信挑战 ID').max(50),
  riskReviewResult: z.string().max(1000).optional().or(z.literal('')),
});
type SubmitFormValues = z.infer<typeof submitSchema>;

const approveSchema = z.object({
  workOrderId: z.string().min(1),
  approvalNo: z
    .string()
    .min(4, '请填写审批单号')
    .max(120),
  approvalNotes: z.string().max(1000).optional().or(z.literal('')),
});
type ApproveFormValues = z.infer<typeof approveSchema>;

const policySchema = z
  .object({
    wechatLoginEnabled: z.boolean(),
    fallbackLoginMethods: z.array(z.enum(['phone_password', 'phone_sms'])),
    customerNoticeRequired: z.boolean(),
    noticeTemplateKey: z.string().max(120).optional().or(z.literal('')),
    changeReason: z.string().min(4, '请填写变更原因').max(1000),
  })
  .refine(
    (v) => v.wechatLoginEnabled || v.fallbackLoginMethods.length > 0,
    {
      message: '禁用微信登录前必须至少保留一种替代登录方式',
      path: ['fallbackLoginMethods'],
    },
  );
type PolicyFormValues = z.infer<typeof policySchema>;

export interface WechatBindingManagementSectionProps {
  readonly permissions: readonly string[];
}

export default function WechatBindingManagementSection({
  permissions,
}: WechatBindingManagementSectionProps): ReactElement | null {
  if (!canReadWechatBindingManagement(permissions)) {
    return null;
  }
  return <AuthorizedSection permissions={permissions} />;
}

function AuthorizedSection({
  permissions,
}: {
  readonly permissions: readonly string[];
}): ReactElement {
  const view = useWechatBindingManagement();
  const canWrite = canWriteWechatBindingWorkOrders(permissions);
  const canApprove = canApproveWechatBindingWorkOrders(permissions);
  const canPolicy = canWriteWechatLoginPolicy(permissions);

  return (
    <section
      className="space-y-3"
      data-testid="settings-wechat-binding-section"
      aria-label="微信登录与绑定管理"
    >
      <header>
        <h3 className="text-lg font-medium">微信登录与绑定管理</h3>
        <p className="text-sm text-muted-foreground">
          管理客户微信绑定状态摘要、解绑/换绑/账号合并复核工单和机构级登录策略。
          本页面仅展示掩码与哈希后缀，永远不会显示原始 OpenID/UnionID/完整手机号。
          解绑、换绑必须走工单 + 客户授权 + 短信验证 + 审批 + 执行，不允许单人直改绑定关系。
        </p>
      </header>

      <BindingsCard view={view} />
      <WorkOrdersCard
        view={view}
        canWrite={canWrite}
        canApprove={canApprove}
      />
      <LoginPolicyCard view={view} canEdit={canPolicy} />
    </section>
  );
}

function BindingsCard({
  view,
}: {
  readonly view: ReturnType<typeof useWechatBindingManagement>;
}): ReactElement {
  const [accountIdFilter, setAccountIdFilter] = useState('');

  function onSearch(): void {
    void view.refreshBindings({
      customerAccountId: accountIdFilter || undefined,
    });
  }

  return (
    <Card data-testid="settings-wechat-binding-bindings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" aria-hidden />
          微信绑定状态摘要
        </CardTitle>
        <CardDescription>
          按客户账号 ID 精确查询；仅显示后端 HMAC 索引的后 8 位哈希尾，永远不显示明文 OpenID。
          客户手机号以 HMAC 索引落盘，其尾数与“手机号后 4 位”毫无对应关系，因此本页面不再提供手机号尾号查询入口。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 md:grid-cols-[1fr_auto]">
          <div className="space-y-1">
            <Label htmlFor="wechat-binding-account">客户账号 ID</Label>
            <Input
              id="wechat-binding-account"
              value={accountIdFilter}
              onChange={(e) => setAccountIdFilter(e.target.value)}
              maxLength={50}
              data-testid="wechat-binding-filter-account"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              onClick={onSearch}
              data-testid="wechat-binding-filter-search"
            >
              查询
            </Button>
          </div>
        </div>
        <BindingsList state={view.bindings} total={view.bindingsTotal} />
      </CardContent>
    </Card>
  );
}

function BindingsList({
  state,
  total,
}: {
  readonly state: AsyncState<readonly AdminWechatBindingSummaryDto[]>;
  readonly total: number;
}): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="wechat-binding-list-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        role="alert"
        data-testid="wechat-binding-list-error"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data || state.data.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="wechat-binding-list-empty"
      >
        未找到符合条件的微信绑定记录。
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="wechat-binding-list">
      <div className="text-xs text-muted-foreground">共 {total} 条</div>
      <ul className="space-y-1">
        {state.data.map((row) => (
          <li
            key={row.id}
            className="rounded border bg-muted/30 px-3 py-2 text-sm"
            data-testid={`wechat-binding-row-${row.id}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-xs">
                acct={row.customerAccountId} provider={row.provider} wx:****{row.openidHashTail}
              </span>
              <span
                className="rounded bg-primary/10 px-2 py-0.5 text-xs"
                data-testid={`wechat-binding-row-${row.id}-status`}
              >
                {STATUS_LABEL[row.status] ?? row.status}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1 text-xs text-muted-foreground">
              {row.nicknameMask && <span>昵称：{row.nicknameMask}</span>}
              <span>绑定时间：{formatDate(row.boundAt)}</span>
              {row.lastLoginAt && <span>最近登录：{formatDate(row.lastLoginAt)}</span>}
              {row.phoneVerifiedAt && <span>手机号已验证</span>}
              {row.pendingWorkOrderId && (
                <span
                  className="text-amber-700"
                  data-testid={`wechat-binding-row-${row.id}-pending`}
                >
                  关联工单：{row.pendingWorkOrderId}
                </span>
              )}
            </div>
            {row.riskFlags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {row.riskFlags.map((flag) => (
                  <span
                    key={flag}
                    className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
                    data-testid={`wechat-binding-row-${row.id}-risk-${flag}`}
                  >
                    <AlertTriangle className="h-3 w-3" aria-hidden />
                    {RISK_FLAG_LABEL[flag] ?? flag}
                  </span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WorkOrdersCard({
  view,
  canWrite,
  canApprove,
}: {
  readonly view: ReturnType<typeof useWechatBindingManagement>;
  readonly canWrite: boolean;
  readonly canApprove: boolean;
}): ReactElement {
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      customerAccountId: '',
      bindingId: '',
      requestType: 'unbind',
      reason: '',
      targetBindingSummary: '',
      authorizationAttachmentIdsText: '',
    },
  });

  const submitForm = useForm<SubmitFormValues>({
    resolver: zodResolver(submitSchema),
    defaultValues: { workOrderId: '', smsChallengeId: '', riskReviewResult: '' },
  });

  const approveForm = useForm<ApproveFormValues>({
    resolver: zodResolver(approveSchema),
    defaultValues: { workOrderId: '', approvalNo: '', approvalNotes: '' },
  });

  async function onCreate(values: CreateFormValues): Promise<void> {
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const attachmentIds = values.authorizationAttachmentIdsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const created = await view.createWorkOrder({
        customerAccountId: values.customerAccountId,
        bindingId: values.bindingId ? values.bindingId : null,
        requestType: values.requestType,
        reason: values.reason,
        targetBindingSummary: values.targetBindingSummary,
        authorizationAttachmentIds: attachmentIds,
      });
      setCreateSuccess(`已创建工单 ${created.id}`);
      createForm.reset();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : '创建失败');
    }
  }

  async function onSubmit(values: SubmitFormValues): Promise<void> {
    setActionError(null);
    try {
      await view.submitWorkOrder(values.workOrderId, {
        smsChallengeId: values.smsChallengeId,
        riskReviewResult: values.riskReviewResult || undefined,
      });
      submitForm.reset();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '提交失败');
    }
  }

  async function onApprove(values: ApproveFormValues): Promise<void> {
    setActionError(null);
    try {
      await view.approveWorkOrder(values.workOrderId, {
        approvalNo: values.approvalNo,
        approvalNotes: values.approvalNotes || undefined,
      });
      approveForm.reset();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '审批失败');
    }
  }

  async function onExecute(id: string): Promise<void> {
    setActionError(null);
    try {
      await view.executeWorkOrder(id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '执行失败');
    }
  }

  return (
    <Card data-testid="settings-wechat-binding-work-orders">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Inbox className="h-4 w-4" aria-hidden />
          微信绑定管理工单
        </CardTitle>
        <CardDescription>
          解绑、换绑、账号合并复核均通过工单驱动；必须先收集客户授权附件、完成短信二次验证、提交审批后才能执行。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {canWrite ? (
          <form
            className="grid gap-2 md:grid-cols-2"
            onSubmit={createForm.handleSubmit(onCreate)}
            data-testid="wechat-binding-create-form"
          >
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-create-account">客户账号 ID</Label>
              <Input
                id="wechat-binding-create-account"
                {...createForm.register('customerAccountId')}
                data-testid="wechat-binding-create-account"
              />
              {createForm.formState.errors.customerAccountId && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-create-account-error"
                >
                  {createForm.formState.errors.customerAccountId.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-create-binding">现有绑定 ID（解绑时必填）</Label>
              <Input
                id="wechat-binding-create-binding"
                {...createForm.register('bindingId')}
                data-testid="wechat-binding-create-binding"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-create-type">工单类型</Label>
              <select
                id="wechat-binding-create-type"
                className="border-input bg-background h-9 w-full rounded-md border px-2 text-sm"
                {...createForm.register('requestType')}
                data-testid="wechat-binding-create-type"
              >
                {REQUEST_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-create-summary">目标绑定脱敏摘要</Label>
              <Input
                id="wechat-binding-create-summary"
                placeholder="例如：wx:****abcd / 138****0000"
                {...createForm.register('targetBindingSummary')}
                data-testid="wechat-binding-create-summary"
              />
              {createForm.formState.errors.targetBindingSummary && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-create-summary-error"
                >
                  {createForm.formState.errors.targetBindingSummary.message}
                </p>
              )}
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="wechat-binding-create-reason">客户授权说明 / 原因</Label>
              <textarea
                id="wechat-binding-create-reason"
                rows={2}
                className="border-input bg-background w-full rounded-md border px-2 py-1 text-sm"
                {...createForm.register('reason')}
                data-testid="wechat-binding-create-reason"
              />
              {createForm.formState.errors.reason && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-create-reason-error"
                >
                  {createForm.formState.errors.reason.message}
                </p>
              )}
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="wechat-binding-create-attachments">
                附件中心 ID（逗号分隔，合并复核必须至少 1 个）
              </Label>
              <Input
                id="wechat-binding-create-attachments"
                {...createForm.register('authorizationAttachmentIdsText')}
                data-testid="wechat-binding-create-attachments"
              />
              {createForm.formState.errors.authorizationAttachmentIdsText && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-create-attachments-error"
                >
                  {createForm.formState.errors.authorizationAttachmentIdsText.message}
                </p>
              )}
            </div>
            <div className="md:col-span-2 flex flex-col gap-1">
              {createError && (
                <p className="text-xs text-destructive" data-testid="wechat-binding-create-error">
                  {createError}
                </p>
              )}
              {createSuccess && (
                <p className="text-xs text-emerald-700" data-testid="wechat-binding-create-success">
                  {createSuccess}
                </p>
              )}
              <Button type="submit" data-testid="wechat-binding-create-submit">
                创建工单
              </Button>
            </div>
          </form>
        ) : (
          <p
            className="text-xs text-muted-foreground"
            data-testid="wechat-binding-create-disabled"
          >
            当前账号未授予 <code>wechat_binding_work_orders:write</code>；如需创建工单，请联系超级管理员。
          </p>
        )}

        {canWrite && (
          <form
            className="grid gap-2 md:grid-cols-3"
            onSubmit={submitForm.handleSubmit(onSubmit)}
            data-testid="wechat-binding-submit-form"
          >
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-submit-workorder">工单 ID</Label>
              <Input
                id="wechat-binding-submit-workorder"
                {...submitForm.register('workOrderId')}
                data-testid="wechat-binding-submit-workorder"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-submit-sms">短信挑战 ID</Label>
              <Input
                id="wechat-binding-submit-sms"
                {...submitForm.register('smsChallengeId')}
                data-testid="wechat-binding-submit-sms"
              />
              {submitForm.formState.errors.smsChallengeId && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-submit-sms-error"
                >
                  {submitForm.formState.errors.smsChallengeId.message}
                </p>
              )}
            </div>
            <div className="space-y-1 md:col-span-3">
              <Label htmlFor="wechat-binding-submit-review">风险复核说明（可选）</Label>
              <Input
                id="wechat-binding-submit-review"
                {...submitForm.register('riskReviewResult')}
                data-testid="wechat-binding-submit-review"
              />
            </div>
            <div className="md:col-span-3">
              <Button type="submit" variant="outline" data-testid="wechat-binding-submit">
                提交工单
              </Button>
            </div>
          </form>
        )}

        {canApprove && (
          <form
            className="grid gap-2 md:grid-cols-3"
            onSubmit={approveForm.handleSubmit(onApprove)}
            data-testid="wechat-binding-approve-form"
          >
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-approve-workorder">工单 ID</Label>
              <Input
                id="wechat-binding-approve-workorder"
                {...approveForm.register('workOrderId')}
                data-testid="wechat-binding-approve-workorder"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-approve-no">审批单号</Label>
              <Input
                id="wechat-binding-approve-no"
                {...approveForm.register('approvalNo')}
                data-testid="wechat-binding-approve-no"
              />
              {approveForm.formState.errors.approvalNo && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-approve-no-error"
                >
                  {approveForm.formState.errors.approvalNo.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-approve-notes">审批意见（可选）</Label>
              <Input
                id="wechat-binding-approve-notes"
                {...approveForm.register('approvalNotes')}
                data-testid="wechat-binding-approve-notes"
              />
            </div>
            <div className="md:col-span-3">
              <Button type="submit" variant="outline" data-testid="wechat-binding-approve">
                审批通过
              </Button>
            </div>
          </form>
        )}

        {actionError && (
          <p className="text-xs text-destructive" data-testid="wechat-binding-action-error">
            {actionError}
          </p>
        )}

        <WorkOrdersList
          state={view.workOrders}
          total={view.workOrdersTotal}
          canApprove={canApprove}
          onExecute={onExecute}
        />
      </CardContent>
    </Card>
  );
}

function WorkOrdersList({
  state,
  total,
  canApprove,
  onExecute,
}: {
  readonly state: AsyncState<readonly AdminWechatBindingWorkOrderDto[]>;
  readonly total: number;
  readonly canApprove: boolean;
  readonly onExecute: (id: string) => Promise<void>;
}): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="wechat-binding-wo-loading">
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        role="alert"
        data-testid="wechat-binding-wo-error"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data || state.data.length === 0) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="wechat-binding-wo-empty">
        暂无工单。
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="wechat-binding-wo-list">
      <div className="text-xs text-muted-foreground">共 {total} 条</div>
      <ul className="space-y-1">
        {state.data.map((wo) => (
          <li
            key={wo.id}
            className="rounded border bg-muted/30 px-3 py-2 text-sm"
            data-testid={`wechat-binding-wo-${wo.id}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-xs">
                {wo.id} · {STATUS_LABEL[wo.requestType] ?? wo.requestType}
              </span>
              <span
                className="rounded bg-primary/10 px-2 py-0.5 text-xs"
                data-testid={`wechat-binding-wo-${wo.id}-status`}
              >
                {STATUS_LABEL[wo.status] ?? wo.status}
              </span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              account={wo.customerAccountId} · summary={wo.targetBindingSummary}
            </div>
            {wo.smsVerifiedAt && (
              <div className="mt-1 text-xs text-muted-foreground">
                短信验证：{formatDate(wo.smsVerifiedAt)}
              </div>
            )}
            {wo.approvalNo && (
              <div className="mt-1 text-xs text-muted-foreground">
                审批单：{wo.approvalNo}
              </div>
            )}
            {wo.riskFlags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {wo.riskFlags.map((flag) => (
                  <span
                    key={flag}
                    className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
                    data-testid={`wechat-binding-wo-${wo.id}-risk-${flag}`}
                  >
                    {RISK_FLAG_LABEL[flag] ?? flag}
                  </span>
                ))}
              </div>
            )}
            {canApprove && wo.status === 'approved' && (
              <div className="mt-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void onExecute(wo.id);
                  }}
                  data-testid={`wechat-binding-wo-${wo.id}-execute`}
                >
                  执行工单
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LoginPolicyCard({
  view,
  canEdit,
}: {
  readonly view: ReturnType<typeof useWechatBindingManagement>;
  readonly canEdit: boolean;
}): ReactElement {
  const initial = view.policy.data;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const form = useForm<PolicyFormValues>({
    resolver: zodResolver(policySchema),
    values: initial
      ? {
          wechatLoginEnabled: initial.wechatLoginEnabled,
          fallbackLoginMethods: [...initial.fallbackLoginMethods] as PolicyFormValues['fallbackLoginMethods'],
          customerNoticeRequired: initial.customerNoticeRequired,
          noticeTemplateKey: initial.noticeTemplateKey ?? '',
          changeReason: '',
        }
      : {
          wechatLoginEnabled: true,
          fallbackLoginMethods: ['phone_password', 'phone_sms'],
          customerNoticeRequired: true,
          noticeTemplateKey: '',
          changeReason: '',
        },
  });

  async function onSubmit(values: PolicyFormValues): Promise<void> {
    setSubmitError(null);
    setSubmitMessage(null);
    try {
      await view.updatePolicy({
        wechatLoginEnabled: values.wechatLoginEnabled,
        fallbackLoginMethods: values.fallbackLoginMethods,
        customerNoticeRequired: values.customerNoticeRequired,
        noticeTemplateKey: values.noticeTemplateKey ? values.noticeTemplateKey : null,
        changeReason: values.changeReason,
      });
      setSubmitMessage('已保存机构微信登录策略');
      form.reset({ ...values, changeReason: '' });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '保存失败');
    }
  }

  const selectedMethods = form.watch('fallbackLoginMethods');
  const enabled = form.watch('wechatLoginEnabled');

  return (
    <Card data-testid="settings-wechat-binding-policy">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4" aria-hidden />
          机构微信登录策略
        </CardTitle>
        <CardDescription>
          关闭微信登录时，必须至少保留一种替代登录方式（手机号 + 密码 或 手机号 + 短信验证码）。
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PolicyState state={view.policy} />
        {canEdit ? (
          <form
            className="mt-3 space-y-3"
            onSubmit={form.handleSubmit(onSubmit)}
            data-testid="wechat-binding-policy-form"
          >
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...form.register('wechatLoginEnabled')}
                data-testid="wechat-binding-policy-enabled"
              />
              开启微信登录
            </label>
            <fieldset
              className="space-y-1"
              data-testid="wechat-binding-policy-fallback"
            >
              <legend className="text-sm font-medium">替代登录方式</legend>
              <div className="flex flex-wrap gap-3 text-sm">
                {FALLBACK_METHOD_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      value={opt.value}
                      checked={selectedMethods.includes(opt.value)}
                      onChange={(e) => {
                        const next = new Set<PolicyFormValues['fallbackLoginMethods'][number]>(
                          selectedMethods,
                        );
                        if (e.target.checked) next.add(opt.value);
                        else next.delete(opt.value);
                        form.setValue('fallbackLoginMethods', Array.from(next), {
                          shouldValidate: true,
                        });
                      }}
                      data-testid={`wechat-binding-policy-fallback-${opt.value}`}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              {form.formState.errors.fallbackLoginMethods && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-policy-fallback-error"
                >
                  {form.formState.errors.fallbackLoginMethods.message ?? ''}
                </p>
              )}
            </fieldset>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...form.register('customerNoticeRequired')}
                data-testid="wechat-binding-policy-notice"
              />
              变更前需要给客户发送通知
            </label>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-policy-template">通知模板键（可选）</Label>
              <Input
                id="wechat-binding-policy-template"
                {...form.register('noticeTemplateKey')}
                data-testid="wechat-binding-policy-template"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="wechat-binding-policy-reason">本次变更原因</Label>
              <Input
                id="wechat-binding-policy-reason"
                {...form.register('changeReason')}
                data-testid="wechat-binding-policy-reason"
              />
              {form.formState.errors.changeReason && (
                <p
                  className="text-xs text-destructive"
                  data-testid="wechat-binding-policy-reason-error"
                >
                  {form.formState.errors.changeReason.message}
                </p>
              )}
            </div>
            {!enabled && selectedMethods.length === 0 && (
              <p
                className="text-xs text-destructive"
                data-testid="wechat-binding-policy-fallback-warning"
              >
                禁用微信登录前必须至少保留一种替代登录方式，否则将阻断客户登录。
              </p>
            )}
            {submitError && (
              <p className="text-xs text-destructive" data-testid="wechat-binding-policy-error">
                {submitError}
              </p>
            )}
            {submitMessage && (
              <p className="text-xs text-emerald-700" data-testid="wechat-binding-policy-success">
                {submitMessage}
              </p>
            )}
            <Button type="submit" data-testid="wechat-binding-policy-save">
              <MessageCircle className="h-4 w-4" aria-hidden />
              保存策略
            </Button>
          </form>
        ) : (
          <p
            className="mt-3 text-xs text-muted-foreground"
            data-testid="wechat-binding-policy-readonly"
          >
            当前账号未授予 <code>wechat_login_policy:write</code>；策略以只读方式展示。
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PolicyState({
  state,
}: {
  readonly state: AsyncState<AdminWechatLoginPolicyDto>;
}): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="wechat-binding-policy-loading">
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        role="alert"
        data-testid="wechat-binding-policy-error-load"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="wechat-binding-policy-empty">
        当前机构尚未配置微信登录策略，默认开启。
      </div>
    );
  }
  const p = state.data;
  return (
    <dl className="grid gap-2 text-sm md:grid-cols-2" data-testid="wechat-binding-policy-current">
      <div>
        <dt className="text-xs uppercase text-muted-foreground">微信登录开关</dt>
        <dd data-testid="wechat-binding-policy-current-enabled">
          {p.wechatLoginEnabled ? '已开启' : '已禁用'}
        </dd>
      </div>
      <div>
        <dt className="text-xs uppercase text-muted-foreground">替代登录方式</dt>
        <dd data-testid="wechat-binding-policy-current-fallback">
          {p.fallbackLoginMethods.length === 0
            ? '—'
            : p.fallbackLoginMethods
                .map(
                  (m) =>
                    FALLBACK_METHOD_OPTIONS.find((o) => o.value === m)?.label ?? m,
                )
                .join('、')}
        </dd>
      </div>
      <div>
        <dt className="text-xs uppercase text-muted-foreground">客户通知要求</dt>
        <dd data-testid="wechat-binding-policy-current-notice">
          {p.customerNoticeRequired ? '需要' : '不需要'}
        </dd>
      </div>
      <div>
        <dt className="text-xs uppercase text-muted-foreground">通知模板键</dt>
        <dd>{p.noticeTemplateKey ?? '—'}</dd>
      </div>
    </dl>
  );
}

function formatDate(value: string): string {
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return value;
  return new Date(ts).toLocaleString();
}
